import { useEffect, useState, useRef } from 'react'
import { useAuth } from '../hooks/useAuth'
import { supabase } from '../lib/supabase'
import { RefreshCw, CheckCircle, XCircle, Clock, ArrowRight, BarChart2, AlertTriangle, Play, Square } from 'lucide-react'
import { format, addMonths, parseISO, startOfMonth } from 'date-fns'

export default function ShopifySync() {
  const { activeBrand, profile } = useAuth()
  const [integration, setIntegration] = useState(null)
  const [locationMaps, setLocationMaps] = useState([])
  const [shopifyLocations, setShopifyLocations] = useState([])
  const [ourLocations, setOurLocations] = useState([])
  const [syncLog, setSyncLog] = useState([])
  const [syncing, setSyncing] = useState(false)
  const [strSyncing, setStrSyncing] = useState(false)
  const [autoBackfillFrom, setAutoBackfillFrom] = useState('2024-01')
  const [autoBackfillTo, setAutoBackfillTo] = useState(format(startOfMonth(new Date()), 'yyyy-MM'))
  const [autoBackfilling, setAutoBackfilling] = useState(false)
  const [autoBackfillProgress, setAutoBackfillProgress] = useState([])
  const [cancelRequested, setCancelRequested] = useState(false)
  const cancelRef = useRef(false)
  const [singleFrom, setSingleFrom] = useState('')
  const [singleUntil, setSingleUntil] = useState('')
  const [singleRunning, setSingleRunning] = useState(false)
  const [loadingShopifyLocs, setLoadingShopifyLocs] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [needsReconnect, setNeedsReconnect] = useState(false)
  const [unknownSkus, setUnknownSkus] = useState([])
  const [ignoringSkus, setIgnoringSkus] = useState({})
  const [timezone, setTimezone] = useState('')
  const [savingTimezone, setSavingTimezone] = useState(false)

  useEffect(() => { if (activeBrand) { load(); setTimezone(activeBrand.timezone || 'America/New_York') } }, [activeBrand])

  async function load() {
    setLoading(true)
    const [{ data: intData }, { data: maps }, { data: locs }, { data: logs }, { data: unknown }] = await Promise.all([
      supabase.from('brand_integrations').select('*').eq('brand_id', activeBrand.id).eq('integration_type', 'shopify').single(),
      supabase.from('shopify_location_map').select('*, locations(name)').eq('brand_id', activeBrand.id),
      supabase.from('locations').select('*').eq('brand_id', activeBrand.id).eq('is_active', true).order('name'),
      supabase.from('shopify_sync_log').select('*').eq('brand_id', activeBrand.id).order('started_at', { ascending: false }).limit(10),
      supabase.from('shopify_unknown_skus').select('*').eq('brand_id', activeBrand.id).eq('ignored', false).order('units_sold_28d', { ascending: false })
    ])
    setIntegration(intData)
    setLocationMaps(maps || [])
    setOurLocations(locs || [])
    setSyncLog(logs || [])
    setUnknownSkus(unknown || [])
    setLoading(false)
  }

  async function saveTimezone() {
    setSavingTimezone(true)
    const { error } = await supabase.from('brands').update({ timezone }).eq('id', activeBrand.id)
    if (error) setError('Failed to save timezone: ' + error.message)
    else setSuccess(`Timezone updated to ${timezone}`)
    setSavingTimezone(false)
  }

  async function fetchShopifyLocations() {
    if (!integration) return
    setLoadingShopifyLocs(true)
    setError('')
    try {
      const res = await fetch('/api/shopify-locations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ brand_id: activeBrand.id })
      })
      const data = await res.json()
      if (data.error) throw new Error(data.error)
      setShopifyLocations(data.locations || [])
    } catch (e) {
      setError('Failed to fetch Shopify locations: ' + e.message)
    }
    setLoadingShopifyLocs(false)
  }

  async function saveLocationMap(shopifyLocId, shopifyLocName, ourLocId) {
    const existing = locationMaps.find(m => m.shopify_location_id === shopifyLocId)
    if (existing) {
      await supabase.from('shopify_location_map').update({ our_location_id: ourLocId || null }).eq('id', existing.id)
    } else {
      await supabase.from('shopify_location_map').insert({
        brand_id: activeBrand.id,
        shopify_location_id: shopifyLocId,
        shopify_location_name: shopifyLocName,
        our_location_id: ourLocId || null
      })
    }
    load()
  }

  async function runSync() {
    setSyncing(true)
    setError('')
    setSuccess('')
    try {
      const res = await fetch('/api/shopify-sync', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ brand_id: activeBrand.id })
      })
      const data = await res.json()
      if (data.error) throw new Error(data.error)
      setSuccess(`Sync complete — ${data.productsChecked} products checked, ${data.corrections} corrections made`)
      load()
    } catch (e) {
      setError('Sync failed: ' + e.message)
    }
    setSyncing(false)
  }

  async function runAutoBackfill() {
    if (!autoBackfillFrom || !autoBackfillTo || autoBackfillFrom > autoBackfillTo) return
    cancelRef.current = false
    setCancelRequested(false)
    setAutoBackfilling(true)
    setError('')
    setSuccess('')

    // Build month list
    const months = []
    let cur = parseISO(autoBackfillFrom + '-01')
    const end = parseISO(autoBackfillTo + '-01')
    while (cur <= end) {
      const sinceDate = format(cur, 'yyyy-MM-dd')
      const nextMonth = addMonths(cur, 1)
      const untilDate = format(nextMonth, 'yyyy-MM-dd')
      months.push({ key: format(cur, 'yyyy-MM'), label: format(cur, 'MMM yyyy'), sinceDate, untilDate, status: 'pending', orders: null, lineItems: null, error: null })
      cur = nextMonth
    }
    setAutoBackfillProgress(months.map(m => ({ ...m })))

    let totalOrders = 0
    for (let i = 0; i < months.length; i++) {
      if (cancelRef.current) {
        setAutoBackfillProgress(prev => prev.map((m, idx) => idx >= i ? { ...m, status: 'cancelled' } : m))
        break
      }
      setAutoBackfillProgress(prev => prev.map((m, idx) => idx === i ? { ...m, status: 'running' } : m))
      try {
        const res = await fetch('/api/shopify-backfill', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ brand_id: activeBrand.id, since_date: months[i].sinceDate, until_date: months[i].untilDate })
        })
        const data = await res.json()
        if (data.error) throw new Error(data.error)
        totalOrders += data.orders_stored
        setAutoBackfillProgress(prev => prev.map((m, idx) => idx === i ? { ...m, status: 'done', orders: data.orders_stored, lineItems: data.line_items_stored } : m))
      } catch (e) {
        setAutoBackfillProgress(prev => prev.map((m, idx) => idx === i ? { ...m, status: 'error', error: e.message } : m))
        // continue with next month
      }
    }

    if (!cancelRef.current) setSuccess(`Backfill complete — ${totalOrders.toLocaleString()} total orders imported`)
    setAutoBackfilling(false)
  }

  async function runSingleBackfill() {
    if (!singleFrom || !singleUntil) return
    setSingleRunning(true)
    setError('')
    setSuccess('')
    try {
      const res = await fetch('/api/shopify-backfill', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ brand_id: activeBrand.id, since_date: singleFrom, until_date: singleUntil })
      })
      const data = await res.json()
      if (data.error) throw new Error(data.error)
      setSuccess(`Done — ${data.orders_stored} orders, ${data.line_items_stored} line items (${data.pages_fetched} pages)`)
    } catch (e) {
      setError('Backfill failed: ' + e.message)
    }
    setSingleRunning(false)
  }

  async function runSTRSync() {
    setStrSyncing(true)
    setError('')
    setSuccess('')
    setNeedsReconnect(false)
    try {
      const res = await fetch('/api/shopify-str-sync', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ brand_id: activeBrand.id })
      })
      const data = await res.json()
      if (data.error) {
        if (data.error.startsWith('MISSING_SCOPE')) {
          setNeedsReconnect(true)
          throw new Error('read_orders permission missing — please reconnect your Shopify store below.')
        }
        throw new Error(data.error)
      }
      setSuccess(`STR sync complete — ${data.productsUpdated} products updated from ${data.skusFound} Shopify SKUs found in last 28 days`)
    } catch (e) {
      setError(e.message)
    }
    setStrSyncing(false)
  }

  async function ignoreSkus(skuIds) {
    const newIgnoring = {}
    skuIds.forEach(id => { newIgnoring[id] = true })
    setIgnoringSkus(prev => ({ ...prev, ...newIgnoring }))
    await supabase.from('shopify_unknown_skus').update({ ignored: true }).in('id', skuIds)
    setUnknownSkus(prev => prev.filter(s => !skuIds.includes(s.id)))
    setIgnoringSkus({})
  }

  if (profile?.role !== 'super_admin' && profile?.role !== 'brand_super_admin') return <div className="empty-state"><p>Super admin access required</p></div>
  if (loading) return <div className="loading-screen"><div className="spinner" /></div>

  const hasMappedLocations = locationMaps.some(m => m.our_location_id)

  return (
    <div className="page">
      <div className="page-header">
        <h1>Shopify Sync — {activeBrand?.name}</h1>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            className="btn btn-secondary"
            onClick={runSTRSync}
            disabled={strSyncing || !integration?.access_token}
            title="Pull last 28 days of fulfilled orders to auto-update sell-through rates"
          >
            <BarChart2 size={14} className={strSyncing ? 'spinning' : ''} />
            {strSyncing ? 'Syncing STR...' : 'Sync Sell-Through Rates'}
          </button>
          <button
            className="btn btn-primary"
            onClick={runSync}
            disabled={syncing || !integration || !hasMappedLocations}
          >
            <RefreshCw size={14} className={syncing ? 'spinning' : ''} />
            {syncing ? 'Syncing...' : 'Run Inventory Sync'}
          </button>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}
      {success && <div style={{ padding: '12px 16px', background: 'var(--success-bg)', border: '1px solid rgba(76,175,125,0.2)', borderRadius: 'var(--radius)', fontSize: '13px', color: 'var(--success)' }}>{success}</div>}

      {/* Reconnect prompt */}
      {needsReconnect && integration && (
        <div style={{ padding: '14px 16px', background: 'var(--warning-bg, #fffbeb)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 'var(--radius)', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <AlertTriangle size={16} style={{ color: 'var(--warning)', flexShrink: 0 }} />
          <div style={{ fontSize: '13px', color: 'var(--text-2)', flex: 1 }}>
            Your Shopify connection needs <strong>read_orders</strong> permission to sync sell-through rates.
          </div>
          <a
            href={`/api/shopify-install?brand_id=${activeBrand?.id}`}
            className="btn btn-secondary btn-sm"
          >
            Reconnect Shopify
          </a>
        </div>
      )}

      {/* Brand Settings */}
      <div className="card">
        <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '12px' }}>Brand Settings</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 4 }}>Store Timezone</div>
            <div style={{ fontSize: 11, color: 'var(--text-3)' }}>Used to bucket orders by the correct local month, matching Shopify Analytics</div>
          </div>
          <select
            className="select"
            value={timezone}
            onChange={e => setTimezone(e.target.value)}
            style={{ width: 240 }}
          >
            {[
              ['America/New_York',    'Eastern (ET) — New York'],
              ['America/Chicago',     'Central (CT) — Chicago'],
              ['America/Denver',      'Mountain (MT) — Denver'],
              ['America/Phoenix',     'Mountain no DST — Phoenix'],
              ['America/Los_Angeles', 'Pacific (PT) — Los Angeles'],
              ['America/Anchorage',   'Alaska (AKT)'],
              ['Pacific/Honolulu',    'Hawaii (HT)'],
              ['UTC',                 'UTC'],
            ].map(([val, label]) => (
              <option key={val} value={val}>{label}</option>
            ))}
          </select>
          <button
            className="btn btn-primary"
            onClick={saveTimezone}
            disabled={savingTimezone || timezone === (activeBrand?.timezone || 'America/New_York')}
          >
            {savingTimezone ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>

      {/* Integration Status */}
      <div className="card">
        <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '12px' }}>Integration Status</div>
        {!integration ? (
          <div className="alert alert-warning">No Shopify integration configured for {activeBrand?.name}.</div>
        ) : !integration.access_token ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div className="alert alert-warning">Not connected yet. Click below to authorize.</div>
            <a href={`/api/shopify-install?brand_id=${activeBrand?.id}`} className="btn btn-primary" style={{ width: 'fit-content' }}>
              Connect Shopify Store
            </a>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
            {[
              { label: 'Store', value: integration.store_url },
              { label: 'Status', value: <span style={{ color: 'var(--success)' }}>✓ Connected</span> },
              { label: 'Last Synced', value: integration.last_synced_at ? format(new Date(integration.last_synced_at), 'MMM d, yyyy h:mm a') : 'Never' },
              { label: 'Granted Scopes', value: integration.granted_scopes
                ? <span style={{ fontSize: 11, wordBreak: 'break-all' }}>
                    {integration.granted_scopes.split(',').map(s => (
                      <span key={s} style={{ display: 'inline-block', background: s.trim() === 'read_all_orders' ? 'var(--success-bg)' : 'var(--bg-4)', color: s.trim() === 'read_all_orders' ? 'var(--success)' : 'var(--text-2)', borderRadius: 4, padding: '1px 6px', margin: '2px', fontSize: 11 }}>{s.trim()}</span>
                    ))}
                  </span>
                : <span style={{ color: 'var(--text-3)' }}>Reconnect to detect</span> },
            ].map(({ label, value }) => (
              <div key={label} style={{ background: 'var(--bg-3)', padding: '12px 14px', borderRadius: 'var(--radius)' }}>
                <div style={{ fontSize: '10px', color: 'var(--text-3)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '4px' }}>{label}</div>
                <div style={{ fontSize: '13px', fontWeight: 500 }}>{value}</div>
              </div>
            ))}
          </div>
        )}

        {/* Reconnect link if connected but needs new scope */}
        {integration?.access_token && (
          <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: '12px', color: 'var(--text-3)' }}>Need to update permissions or reconnect?</span>
            <a href={`/api/shopify-install?brand_id=${activeBrand?.id}`} className="btn btn-ghost btn-sm">
              Reconnect / Update Scopes
            </a>
          </div>
        )}
      </div>

      {/* Order History Backfill */}
      {integration?.access_token && (
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 600 }}>Order History Backfill</div>
              <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 2 }}>
                Import historical orders month by month. Each month runs as a separate request to avoid timeouts. Existing records are overwritten with corrected values.
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, marginLeft: 16 }}>
              <input
                type="month"
                className="input"
                value={autoBackfillFrom}
                max={autoBackfillTo}
                onChange={e => setAutoBackfillFrom(e.target.value)}
                disabled={autoBackfilling}
                style={{ fontSize: 13, padding: '5px 10px', width: 140 }}
              />
              <span style={{ color: 'var(--text-3)', fontSize: 13 }}>→</span>
              <input
                type="month"
                className="input"
                value={autoBackfillTo}
                min={autoBackfillFrom}
                max={format(startOfMonth(new Date()), 'yyyy-MM')}
                onChange={e => setAutoBackfillTo(e.target.value)}
                disabled={autoBackfilling}
                style={{ fontSize: 13, padding: '5px 10px', width: 140 }}
              />
              {autoBackfilling ? (
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    cancelRef.current = true
                    setCancelRequested(true)
                    setAutoBackfillProgress(prev => prev.map(m => m.status === 'pending' ? { ...m, status: 'cancelled' } : m))
                  }}
                  disabled={cancelRequested}
                  style={{ color: 'var(--danger)', borderColor: 'var(--danger)' }}
                >
                  <Square size={13} /> {cancelRequested ? 'Stopping after this month…' : 'Cancel'}
                </button>
              ) : (
                <button
                  className="btn btn-primary"
                  onClick={runAutoBackfill}
                  disabled={!autoBackfillFrom || !autoBackfillTo || autoBackfillFrom > autoBackfillTo}
                >
                  <Play size={13} /> Run Backfill
                </button>
              )}
            </div>
          </div>

          {/* Single date-range override for large months that timeout */}
          <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 12, color: 'var(--text-3)', flexShrink: 0 }}>Single range (for large months that timeout):</span>
            <input type="date" className="input" value={singleFrom} onChange={e => setSingleFrom(e.target.value)} disabled={singleRunning} style={{ fontSize: 13, padding: '4px 8px', width: 140 }} />
            <span style={{ color: 'var(--text-3)', fontSize: 13 }}>→</span>
            <input type="date" className="input" value={singleUntil} onChange={e => setSingleUntil(e.target.value)} disabled={singleRunning} style={{ fontSize: 13, padding: '4px 8px', width: 140 }} />
            <button className="btn btn-secondary" onClick={runSingleBackfill} disabled={singleRunning || !singleFrom || !singleUntil || singleFrom >= singleUntil} style={{ fontSize: 12 }}>
              {singleRunning ? 'Running…' : 'Run'}
            </button>
          </div>

          {autoBackfillProgress.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {autoBackfillProgress.map(m => {
                const statusStyle = {
                  pending:   { bg: 'var(--bg-4)', color: 'var(--text-3)' },
                  running:   { bg: 'rgba(108,142,247,0.12)', color: 'var(--accent)' },
                  done:      { bg: 'rgba(76,175,125,0.12)', color: 'var(--success)' },
                  error:     { bg: 'rgba(239,68,68,0.1)', color: 'var(--danger)' },
                  cancelled: { bg: 'var(--bg-4)', color: 'var(--text-3)' },
                }[m.status] || {}
                return (
                  <div key={m.key} title={m.error || (m.orders != null ? `${m.orders} orders, ${m.lineItems} line items` : '')} style={{ background: statusStyle.bg, color: statusStyle.color, borderRadius: 6, padding: '6px 10px', fontSize: 12, fontWeight: 500, minWidth: 90, textAlign: 'center' }}>
                    <div>{m.label}</div>
                    <div style={{ fontSize: 11, marginTop: 2, opacity: 0.85 }}>
                      {m.status === 'pending' && '—'}
                      {m.status === 'running' && '⟳ running'}
                      {m.status === 'done' && `✓ ${(m.orders || 0).toLocaleString()} orders`}
                      {m.status === 'error' && '✕ error'}
                      {m.status === 'cancelled' && 'cancelled'}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* Location Mapping */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <div>
            <div style={{ fontSize: '14px', fontWeight: 600 }}>Location Mapping</div>
            <div style={{ fontSize: '12px', color: 'var(--text-3)', marginTop: '2px' }}>Map your Shopify locations to locations in this app</div>
          </div>
          <button className="btn btn-secondary btn-sm" onClick={fetchShopifyLocations} disabled={loadingShopifyLocs || !integration}>
            <RefreshCw size={13} />
            {loadingShopifyLocs ? 'Loading...' : 'Load Shopify Locations'}
          </button>
        </div>

        {shopifyLocations.length === 0 && locationMaps.length === 0 ? (
          <div style={{ color: 'var(--text-3)', fontSize: '13px' }}>Click "Load Shopify Locations" to fetch your Shopify locations and map them.</div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Shopify Location</th><th></th><th>Maps To</th></tr>
              </thead>
              <tbody>
                {(shopifyLocations.length > 0 ? shopifyLocations : locationMaps.map(m => ({ id: m.shopify_location_id, name: m.shopify_location_name }))).map(loc => {
                  const map = locationMaps.find(m => m.shopify_location_id === loc.id)
                  return (
                    <tr key={loc.id}>
                      <td style={{ fontWeight: 500 }}>{loc.name}</td>
                      <td style={{ color: 'var(--text-3)' }}><ArrowRight size={14} /></td>
                      <td>
                        <select
                          className="select"
                          style={{ width: '220px' }}
                          value={map?.our_location_id || ''}
                          onChange={e => saveLocationMap(loc.id, loc.name, e.target.value || null)}
                        >
                          <option value="">— Not mapped (ignore) —</option>
                          {ourLocations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                        </select>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
        {!hasMappedLocations && locationMaps.length > 0 && (
          <div className="alert alert-warning" style={{ marginTop: '12px' }}>Map at least one location to enable syncing.</div>
        )}
      </div>

      {/* SKU Coverage */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <div>
            <div style={{ fontSize: '14px', fontWeight: 600 }}>Shopify SKU Coverage</div>
            <div style={{ fontSize: '12px', color: 'var(--text-3)', marginTop: '2px' }}>SKUs with Shopify inventory that aren't in your catalog</div>
          </div>
          {unknownSkus.filter(s => s.units_sold_28d > 0 || s.shopify_qty > 0).length > 0 && (
            <button className="btn btn-ghost btn-sm" style={{ color: 'var(--text-3)' }} onClick={() => ignoreSkus(unknownSkus.map(s => s.id))}>
              Ignore All
            </button>
          )}
        </div>
        {unknownSkus.filter(s => s.units_sold_28d > 0 || s.shopify_qty > 0).length === 0 ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 14px', background: 'var(--success-bg)', borderRadius: 'var(--radius)', fontSize: '13px', color: 'var(--success)' }}>
            <span>✓</span> All Shopify SKUs with sales in the last 28 days are in your catalog
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>SKU</th><th>Units Sold (28d)</th><th>Shopify Qty</th><th>First Seen</th><th>Last Seen</th><th></th></tr>
              </thead>
              <tbody>
                {unknownSkus.filter(s => s.units_sold_28d > 0 || s.shopify_qty > 0).map(s => (
                  <tr key={s.id}>
                    <td><span className="mono">{s.sku}</span></td>
                    <td style={{ fontWeight: 600, color: s.units_sold_28d > 0 ? 'var(--warning)' : 'var(--text-3)' }}>{s.units_sold_28d || '—'}</td>
                    <td style={{ fontWeight: 600, color: s.shopify_qty > 0 ? 'var(--info)' : 'var(--text-3)' }}>{s.shopify_qty || '—'}</td>
                    <td style={{ fontSize: '12px', color: 'var(--text-3)' }}>{format(new Date(s.first_seen_at), 'MMM d, yyyy')}</td>
                    <td style={{ fontSize: '12px', color: 'var(--text-3)' }}>{format(new Date(s.last_seen_at), 'MMM d, yyyy')}</td>
                    <td>
                      <button
                        className="btn btn-ghost btn-sm"
                        style={{ fontSize: '11px', color: 'var(--text-3)' }}
                        onClick={() => ignoreSkus([s.id])}
                        disabled={ignoringSkus[s.id]}
                      >
                        {ignoringSkus[s.id] ? '...' : 'Ignore'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ padding: '10px 12px', fontSize: '12px', color: 'var(--text-3)' }}>
              Add missing SKUs to your Catalog, then run Inventory Sync to clear them. "Ignore" hides SKUs you don't want to track (e.g. gift cards).
            </div>
          </div>
        )}
      </div>

      {/* How it works */}
      <div className="card" style={{ background: 'var(--accent-bg)', borderColor: 'rgba(108,142,247,0.2)' }}>
        <div style={{ fontSize: '13px', color: 'var(--text-2)', lineHeight: 1.7 }}>
          <strong style={{ color: 'var(--accent)' }}>How syncing works:</strong><br />
          <strong>Inventory Sync</strong> — fetches live quantities from Shopify and creates correction transactions to reconcile differences.<br />
          <strong>Sell-Through Rate Sync</strong> — pulls last 28 days of fulfilled orders, calculates units sold per SKU, and auto-updates sell-through rates. Rates updated this way show an <span style={{ background: 'var(--accent)', color: 'white', padding: '1px 5px', borderRadius: '4px', fontSize: '11px' }}>Auto</span> badge in Inventory.
        </div>
      </div>

      {/* Sync Log */}
      <div className="card">
        <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '16px' }}>Sync History</div>
        {syncLog.length === 0 ? (
          <div style={{ color: 'var(--text-3)', fontSize: '13px' }}>No syncs run yet.</div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead><tr><th>Started</th><th>Type</th><th>Status</th><th>Products</th><th>SKUs w/ Sales</th><th>Duration</th><th>Error</th></tr></thead>
              <tbody>
                {syncLog.map(log => (
                  <tr key={log.id}>
                    <td style={{ fontSize: '12px' }}>{format(new Date(log.started_at), 'MMM d, yyyy h:mm a')}</td>
                    <td>
                      <span className={`badge ${log.sync_type === 'str' ? 'badge-info' : 'badge-accent'}`} style={{ fontSize: '10px' }}>
                        {log.sync_type === 'str' ? 'STR' : 'Inventory'}
                      </span>
                    </td>
                    <td>
                      <span className={`badge ${log.status === 'success' ? 'badge-success' : log.status === 'error' ? 'badge-danger' : 'badge-neutral'}`}>
                        {log.status === 'success' ? <CheckCircle size={10} /> : log.status === 'error' ? <XCircle size={10} /> : <Clock size={10} />}
                        {' '}{log.status}
                      </span>
                    </td>
                    <td>{log.products_synced ?? '—'}</td>
                    <td style={{ color: 'var(--text-3)' }}>{log.sync_type === 'str' ? (log.corrections_made ?? '—') : '—'}</td>
                    <td style={{ fontSize: '12px', color: 'var(--text-3)' }}>
                      {log.completed_at ? `${Math.round((new Date(log.completed_at) - new Date(log.started_at)) / 1000)}s` : '—'}
                    </td>
                    <td style={{ fontSize: '12px', color: 'var(--danger)', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{log.error_message || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <style>{`.spinning { animation: spin 1s linear infinite; } @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  )
}
